import dayjs from 'dayjs';
import type { i18n as I18n, TFunction } from 'i18next';

export interface AuditEntry {
    id: string;
    restaurantId: string;
    locationId: string | null;
    actorId: string | null;
    actorName: string;
    actorRole: string;
    action: string;
    entityType: string;
    entityId: string | null;
    summary: { label: string; from?: string | number | boolean | null; to?: string | number | boolean | null };
    createdAt: string;
}

export interface AuditPage {
    items: AuditEntry[];
    nextCursor: string | null;
}

/** Filter areas and the action names each one covers (API_CONTRACT_1_1.md, section 8.2). */
export const AREAS = {
    orders: ['order.created', 'order.status', 'order.payment_status', 'order.payment_method', 'order.note', 'order.deleted'],
    menu: [
        'menu.created',
        'menu.updated',
        'menu.deleted',
        'menu.activated',
        'category.created',
        'category.updated',
        'category.deleted',
        'category.reordered',
    ],
    items: ['item.created', 'item.updated', 'item.deleted', 'item.image'],
    availability: ['item.availability'],
    paused: ['location.pause'],
    tables: ['table.created', 'table.updated', 'table.deleted', 'table.bulk_created'],
    team: ['user.created', 'user.updated', 'user.deleted', 'user.role', 'user.active', 'user.password'],
    venue: [
        'location.created',
        'location.updated',
        'location.deleted',
        'restaurant.updated',
        'restaurant.logo',
        'restaurant.timezone',
        'restaurant.plan',
        'restaurant.trial',
        'restaurant.fee_rate',
    ],
    signin: ['session.login'],
} as const satisfies Record<string, readonly string[]>;

export type Area = keyof typeof AREAS;
export const AREA_IDS = Object.keys(AREAS) as Area[];
export const isArea = (v: string | null): v is Area => v !== null && v in AREAS;

/** Areas whose rows belong to a location; the others are venue-wide and carry no location. */
export const LOCATION_AREAS: readonly Area[] = ['orders', 'paused', 'tables'];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/** Which sentence of a pair or trio applies: "item.availability" + to=false is "item.availability_off". */
const variant = ({ action, summary: { from, to } }: AuditEntry): string => {
    switch (action) {
        case 'order.created':
            return to === 'table' || to === 'togo' ? `_${to}` : '';
        case 'order.payment_status':
            return to === 'paid' || to === 'unpaid' ? `_${to}` : '';
        case 'order.note':
            return to == null ? '_removed' : from == null ? '_added' : '';
        case 'item.availability':
            return typeof to === 'boolean' ? (to ? '_on' : '_off') : '';
        case 'user.active':
            return typeof to === 'boolean' ? (to ? '_on' : '_off') : '';
        case 'location.pause':
            return typeof to === 'boolean' ? (to ? '_resumed' : '_paused') : '';
        case 'menu.activated':
            return to == null ? '_cleared' : from == null ? '_set' : '';
        case 'table.updated':
            return to == null ? '_togo' : from == null ? '_labelled' : '';
        case 'item.image':
        case 'restaurant.logo':
            return to === 'set' || to === 'removed' ? `_${to}` : '';
        default:
            return '';
    }
};

/** Turns an audit row into one translated sentence. Unknown actions get the generic sentence, never a raw key. */
export const describe = (entry: AuditEntry, t: TFunction<'activity'>, i18n: I18n): string => {
    const has = (key: string) => i18n.exists(key, { ns: 'activity' });
    const value = (v: string | number | boolean | null | undefined): string => {
        if (v === null || v === undefined) return t('values.none');
        if (typeof v !== 'string') return String(v);
        if (ISO_DATE.test(v)) return dayjs(v).format('D MMM YYYY');
        const key = [`values.${v}`, `common:status.${v}`, `common:plans.${v}`].find(has);
        return key ? t(key as 'values.none') : v;
    };
    const { label, from, to } = entry.summary;
    const actor = entry.actorId === null && has(`values.${entry.actorRole}`) ? t(`values.${entry.actorRole}` as 'values.none') : entry.actorName;
    const vars = { actor, label, from: value(from), to: value(to) };
    const base = `actions.${entry.action}`;
    const key = [`${base}${variant(entry)}`, base].find(has);
    if (key) return t(key as 'generic', vars);
    return t('generic', { ...vars, entity: t(has(`entity.${entry.entityType}`) ? (`entity.${entry.entityType}` as 'entity.other') : 'entity.other') });
};

export interface Place {
    to: string;
    kind: 'order' | 'item' | 'menu' | 'location' | 'user' | 'restaurant';
}

/** Where the row's subject lives in the panel. null when it is gone, has no page, or the role may not open it. */
export const placeOf = (e: AuditEntry, canManage: boolean): Place | null => {
    if (e.action.endsWith('.deleted')) return null;
    const id = e.entityId;
    switch (e.entityType) {
        case 'order':
            return id ? { to: `/orders/${id}`, kind: 'order' } : null;
        case 'item':
            return id ? { to: `/menu-items/${id}`, kind: 'item' } : null;
        case 'menu':
            return id ? { to: `/menus/${id}`, kind: 'menu' } : null;
        case 'location':
            return id ? { to: `/locations/${id}`, kind: 'location' } : null;
        case 'table':
            return e.locationId ? { to: `/locations/${e.locationId}`, kind: 'location' } : null;
        case 'user':
            return id && canManage ? { to: `/users/edit/${id}`, kind: 'user' } : null;
        case 'restaurant':
            return id && canManage ? { to: `/restaurants/${id}`, kind: 'restaurant' } : null;
        default:
            return null;
    }
};
